/**
 * My Notifications Card Component
 * 
 * Personal notification preferences for the Settings page.
 * Users can enable/disable notification types and manage mutes.
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Bell, 
  Mail, 
  ChevronDown, 
  ChevronUp,
  AlertTriangle,
  VolumeX,
  Volume2,
  ShieldCheck,
  RefreshCw,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useResolvedUserNotificationPreferences,
  useUpdateUserPreference,
  useNotificationMutes,
  useAddMute,
  useRemoveMute,
} from '@/hooks/notifications';
import { useMyProfile } from '@/hooks/profile';
import { 
  NOTIFICATION_TYPES, 
  NOTIFICATION_TYPE_METADATA,
  type NotificationTypeKey,
} from '@/constants/notificationTypes';

interface MyNotificationsCardProps {
  className?: string;
}

// Get human-readable label for a notification type (prefers selfLabel for personal context)
function getTypeLabel(typeKey: NotificationTypeKey): string {
  const meta = NOTIFICATION_TYPE_METADATA[typeKey];
  if (meta?.selfLabel) return meta.selfLabel;
  if (meta?.description) return meta.description;
  
  const event = typeKey.split('.')[0];
  return event.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Get category label
function getCategoryLabel(typeKey: string): string {
  const category = typeKey.split('.')[0];
  return category.charAt(0).toUpperCase() + category.slice(1);
}

export function MyNotificationsCard({ className }: MyNotificationsCardProps) {
  const { data: profile } = useMyProfile();
  const profileId = profile?.id;
  const companyId = profile?.company_id;
  
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Fetch resolved preferences (handles role + wildcard inheritance) and mutes
  // Note: RPC uses auth.uid() directly - no need to pass profileId
  const { 
    data: resolvedPrefs, 
    isLoading: prefsLoading, 
    isError: prefsError,
    refetch: refetchPrefs,
    isFetching: prefsFetching,
  } = useResolvedUserNotificationPreferences();
  const { 
    data: mutes, 
    isLoading: mutesLoading,
    isError: mutesError,
    refetch: refetchMutes,
    isFetching: mutesFetching,
  } = useNotificationMutes(profileId, companyId);
  
  // Mutations
  const { mutate: updatePreference, isPending: isUpdating } = useUpdateUserPreference(profileId, companyId);
  const { mutate: addMute, isPending: isAddingMute } = useAddMute(profileId, companyId);
  const { mutate: removeMute, isPending: isRemovingMute } = useRemoveMute(profileId);
  
  const isLoading = prefsLoading || mutesLoading;
  const isMutating = isUpdating || isAddingMute || isRemovingMute;
  const hasError = prefsError || mutesError;
  const isRefetching = prefsFetching || mutesFetching;
  
  // Handle retry
  const handleRetry = () => {
    if (prefsError) refetchPrefs();
    if (mutesError) refetchMutes();
  };
  
  // Build preference state from resolved preferences (already handles role + wildcard)
  const preferenceState = useMemo(() => {
    const allTypes = (Object.values(NOTIFICATION_TYPES) as NotificationTypeKey[])
      .filter(t => !NOTIFICATION_TYPE_METADATA[t]?.hiddenFromPersonalSettings);
    const state: Record<string, { enabled: boolean; channels: string[]; isMuted: boolean; isForceEnabled: boolean }> = {};
    
    for (const typeKey of allTypes) {
      // Get resolved preference (already includes role/wildcard inheritance)
      const resolved = resolvedPrefs?.find(p => p.type_key === typeKey);
      const mute = mutes?.find(m => m.type_key === typeKey || 
        (m.type_key.endsWith('.*') && typeKey.startsWith(m.type_key.slice(0, -2))));
      
      const meta = NOTIFICATION_TYPE_METADATA[typeKey];
      
      state[typeKey] = {
        enabled: resolved?.force_enabled ? true : (resolved?.enabled ?? true),
        channels: resolved?.force_enabled 
          ? (resolved?.channels ?? meta?.defaultChannels ?? ['in_app'])
          : (resolved?.channels ?? meta?.defaultChannels ?? ['in_app']),
        // Force-enabled notifications bypass mutes on backend, so UI should reflect that
        isMuted: resolved?.force_enabled ? false : !!mute,
        isForceEnabled: resolved?.force_enabled ?? false,
      };
    }
    
    return state;
  }, [resolvedPrefs, mutes]);
  
  // Group by category for collapsed view
  const categories = useMemo(() => {
    const allTypes = (Object.values(NOTIFICATION_TYPES) as NotificationTypeKey[])
      .filter(t => !NOTIFICATION_TYPE_METADATA[t]?.hiddenFromPersonalSettings);
    const groups: Record<string, NotificationTypeKey[]> = {};
    
    for (const type of allTypes) {
      const category = type.split('.')[0];
      if (!groups[category]) groups[category] = [];
      groups[category].push(type);
    }
    
    return groups;
  }, []);
  
  // Handle preference toggle (main switch)
  // Preserves channel selections when toggling off/on
  const handleToggle = (typeKey: string, enabled: boolean) => {
    const current = preferenceState[typeKey];
    const currentChannels = current?.channels || [];
    
    updatePreference({
      type_key: typeKey,
      enabled,
      // Preserve channels when disabling; restore (or default) when enabling
      channels: enabled 
        ? (currentChannels.length > 0 ? currentChannels : ['in_app'])
        : currentChannels,
    });
  };
  
  // Handle channel toggle
  const handleChannelToggle = (typeKey: string, channel: string) => {
    const current = preferenceState[typeKey];
    
    // Block channel removal for force-enabled (required) notifications
    if (current.isForceEnabled && current.channels.includes(channel) && current.channels.length === 1) {
      return; // Cannot remove last channel from required notification
    }
    
    const hasChannel = current.channels.includes(channel);
    const newChannels = hasChannel
      ? current.channels.filter(c => c !== channel)
      : [...current.channels, channel];
    
    // Derive enabled state from channels: empty = disabled, non-empty = enabled
    const newEnabled = newChannels.length > 0;
    
    updatePreference({
      type_key: typeKey,
      enabled: newEnabled,
      channels: newChannels,
    });
  };
  
  // Handle mute toggle
  const handleMuteToggle = (typeKey: string) => {
    const current = preferenceState[typeKey];
    if (current.isMuted) {
      removeMute(typeKey);
    } else {
      addMute({ typeKey });
    }
  };
  
  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }
  
  // Error state - show alert and disable controls
  if (hasError) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            <CardTitle as="h2">My Notifications</CardTitle>
          </div>
          <CardDescription>
            Choose which notifications you want to receive and how
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Unable to load preferences</AlertTitle>
            <AlertDescription className="mt-2">
              <p className="mb-3">
                Your notification preferences could not be loaded. Please check your connection and try again.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRetry}
                disabled={isRefetching}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`} />
                {isRefetching ? 'Retrying...' : 'Retry'}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }
  
  // Count effectively enabled notifications (has channels and not muted)
  const enabledCount = Object.values(preferenceState).filter(
    p => p.enabled && p.channels.length > 0 && !p.isMuted
  ).length;
  const totalCount = Object.keys(preferenceState).length;
  
  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            <CardTitle as="h2">My Notifications</CardTitle>
          </div>
          <Badge variant="secondary">
            {enabledCount}/{totalCount} enabled
          </Badge>
        </div>
        <CardDescription>
          Choose which notifications you want to receive and how
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick summary for collapsed view */}
        {!isExpanded && (
          <div className="space-y-3">
            {Object.entries(categories).map(([category, types]) => {
              const enabledInCategory = types.filter(t => 
                preferenceState[t]?.enabled && 
                preferenceState[t]?.channels.length > 0 && 
                !preferenceState[t]?.isMuted
              ).length;
              
              return (
                <div key={category} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <span className="font-medium">{getCategoryLabel(category)}</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      {enabledInCategory}/{types.length} enabled
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Bell className="h-4 w-4" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Expanded detailed view */}
        {isExpanded && (
          <div className="space-y-6">
            {Object.entries(categories).map(([category, types]) => (
              <div key={category}>
                <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">
                  {getCategoryLabel(category)}
                </h3>
                <div className="space-y-3">
                  {types.map(typeKey => {
                    const pref = preferenceState[typeKey];
                    if (!pref) return null;
                    
                    return (
                      <div
                        key={typeKey}
                        className={`flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 p-3 rounded-lg border bg-card transition-opacity ${
                          pref.channels.length === 0 ? 'opacity-60' : ''
                        }`}
                      >
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{getTypeLabel(typeKey)}</span>
                            {pref.isForceEnabled && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="secondary" className="text-xs cursor-help">
                                      <ShieldCheck className="h-3 w-3 mr-1" />
                                      Managed by organization
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-xs">
                                    <p className="text-sm">
                                      This notification is required by your organization. 
                                      Delivery channels are set by your administrator.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {pref.isMuted && (
                              <Badge variant="secondary" className="text-xs">
                                <VolumeX className="h-3 w-3 mr-1" />
                                Muted
                              </Badge>
                            )}
                          </div>
                          
                          {/* Channel toggles - disabled entirely when force-enabled */}
                          <div className="flex items-center gap-4">
                            <label className={`flex items-center gap-1.5 text-xs ${
                              pref.isForceEnabled ? 'text-muted-foreground/60' : 'text-muted-foreground'
                            }`}>
                              <Checkbox
                                checked={pref.channels.includes('in_app')}
                                onCheckedChange={() => handleChannelToggle(typeKey, 'in_app')}
                                disabled={pref.isForceEnabled || pref.isMuted || isMutating}
                              />
                              <Bell className="h-3 w-3" />
                              In-app
                            </label>
                            
                            {/* Mute button */}
                            {!pref.isForceEnabled && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() => handleMuteToggle(typeKey)}
                                disabled={isMutating}
                              >
                                {pref.isMuted ? (
                                  <>
                                    <Volume2 className="h-3 w-3 mr-1" />
                                    Unmute
                                  </>
                                ) : (
                                  <>
                                    <VolumeX className="h-3 w-3 mr-1" />
                                    Mute
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Enable switch - reflects whether any channels are active */}
                        <div className="flex items-center sm:self-start">
                          <Switch
                            checked={pref.enabled && pref.channels.length > 0}
                            onCheckedChange={(checked) => handleToggle(typeKey, checked)}
                            disabled={pref.isForceEnabled || pref.isMuted || isMutating}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Expand/collapse button */}
        <Button
          variant="ghost"
          className="w-full"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-4 w-4 mr-2" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-2" />
              Customize notifications
            </>
          )}
        </Button>
        
        {/* Info text */}
        <p className="text-xs text-muted-foreground text-center">
          Some notifications may be required by your organization and cannot be disabled.
        </p>
      </CardContent>
    </Card>
  );
}
